import React, { useCallback, useEffect, useRef, useState } from "react";
import Cropper from "react-easy-crop";
import { Button } from "../ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../ui/dialog";
import {
  Camera,
  Upload,
  Trash2,
  RefreshCw,
  Loader2,
  AlertTriangle,
  Crop as CropIcon,
} from "lucide-react";
import { toast } from "sonner";
import { profileAPI, resolveAssetUrl } from "../../services/api";
import { cropImageToBlob } from "../../lib/cropImage";

/**
 * Photo Upload Modal — flows:
 *   1. Take a photo with camera (primary CTA, capability-detected)
 *   2. Upload a photo from disk
 *   3. Re-crop the existing original (no re-upload required)
 *   4. Remove the current photo
 *
 * After capture/file pick, the candidate enters a **pan + zoom crop
 * step** so they can frame themselves before saving. Both the cropped
 * square AND the uncropped original are uploaded so they can re-crop
 * later without quality loss.
 */
export default function PhotoUploadModal({
  open,
  onOpenChange,
  hasPhoto,
  originalUrl,
  onChange,
}) {
  const [mode, setMode] = useState("choose"); // choose | camera | crop
  const [cameraError, setCameraError] = useState("");
  const [sourceUrl, setSourceUrl] = useState(""); // URL/dataURL fed to the cropper
  const [originalBlob, setOriginalBlob] = useState(null); // raw upload preserved for re-crop
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);
  const [busy, setBusy] = useState(false);

  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const streamRef = useRef(null);
  const fileInputRef = useRef(null);

  const cameraSupported =
    typeof navigator !== "undefined" &&
    !!navigator.mediaDevices &&
    typeof navigator.mediaDevices.getUserMedia === "function" &&
    typeof window !== "undefined" &&
    (window.isSecureContext ||
      window.location.hostname === "localhost" ||
      window.location.hostname === "127.0.0.1");

  useEffect(() => {
    if (!open) {
      stopCamera();
      setMode("choose");
      setSourceUrl("");
      setOriginalBlob(null);
      setCrop({ x: 0, y: 0 });
      setZoom(1);
      setCroppedAreaPixels(null);
      setCameraError("");
      setBusy(false);
    }
  }, [open]);

  const stopCamera = () => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    }
  };

  const enterCropWithBlob = (blob) => {
    setOriginalBlob(blob);
    setSourceUrl(URL.createObjectURL(blob));
    setCrop({ x: 0, y: 0 });
    setZoom(1);
    setMode("crop");
  };

  const recropExisting = async () => {
    if (!originalUrl) return;
    try {
      setBusy(true);
      const resolved = resolveAssetUrl(originalUrl);
      const res = await fetch(resolved);
      const blob = await res.blob();
      // For re-crop the existing original IS the source; we don't
      // re-upload it as a new original on save.
      setOriginalBlob(null);
      setSourceUrl(URL.createObjectURL(blob));
      setCrop({ x: 0, y: 0 });
      setZoom(1);
      setMode("crop");
    } catch (e) {
      toast.error("Couldn't load the original photo. Try uploading a new one.");
    } finally {
      setBusy(false);
    }
  };

  const startCamera = async () => {
    setCameraError("");
    if (!cameraSupported) {
      setCameraError(
        "Camera isn't available in this browser. Try Safari or Chrome on your phone, or upload a photo instead."
      );
      return;
    }
    setMode("camera");
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: "user", width: { ideal: 1280 }, height: { ideal: 1280 } },
        audio: false,
      });
      streamRef.current = stream;
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        await videoRef.current.play().catch(() => {});
      }
    } catch (e) {
      console.error("getUserMedia failed:", e);
      let msg;
      if (e?.name === "NotAllowedError" || e?.name === "PermissionDeniedError") {
        msg = "Camera permission was denied. Allow camera access in your browser's address bar, or use Upload Photo instead.";
      } else if (e?.name === "NotFoundError" || e?.name === "DevicesNotFoundError") {
        msg = "No camera detected on this device. Use Upload Photo instead.";
      } else if (e?.name === "NotReadableError") {
        msg = "Your camera is busy in another app. Close it and try again.";
      } else {
        msg = "Couldn't access the camera on this device. Try Upload Photo instead.";
      }
      setCameraError(msg);
      setMode("choose");
    }
  };

  const capturePhoto = () => {
    const video = videoRef.current;
    const canvas = canvasRef.current;
    if (!video || !canvas) return;
    const w = video.videoWidth || 720;
    const h = video.videoHeight || 720;
    canvas.width = w;
    canvas.height = h;
    const ctx = canvas.getContext("2d");
    ctx.drawImage(video, 0, 0, w, h);
    canvas.toBlob(
      (blob) => {
        if (!blob) {
          toast.error("Capture failed. Please try again.");
          return;
        }
        stopCamera();
        enterCropWithBlob(blob);
      },
      "image/jpeg",
      0.92
    );
  };

  const onFilePicked = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!/^image\/(jpe?g|png|webp)$/i.test(file.type)) {
      toast.error("Please choose a JPG, PNG, or WebP image.");
      return;
    }
    if (file.size > 8 * 1024 * 1024) {
      toast.error("Image must be smaller than 8MB.");
      return;
    }
    enterCropWithBlob(file);
    // Reset the input so picking the same file again triggers onChange.
    e.target.value = "";
  };

  const onCropComplete = useCallback((_area, areaPixels) => {
    setCroppedAreaPixels(areaPixels);
  }, []);

  const handleSaveCrop = async () => {
    if (!sourceUrl || !croppedAreaPixels) return;
    try {
      setBusy(true);
      const croppedBlob = await cropImageToBlob(sourceUrl, croppedAreaPixels, 768);
      const croppedFile = new File([croppedBlob], `photo-${Date.now()}.jpg`, {
        type: "image/jpeg",
      });
      // Only send the `original` field when this crop session started
      // from a fresh capture/upload — when re-cropping the existing
      // stored original we already have it in the bucket.
      const originalFile = originalBlob
        ? new File([originalBlob], `photo-original-${Date.now()}.jpg`, {
            type: originalBlob.type || "image/jpeg",
          })
        : null;
      const res = await profileAPI.uploadPhoto(croppedFile, originalFile);
      toast.success("Photo updated.");
      onChange?.({
        avatar_url: res.avatar_url,
        avatar_original_url: res.avatar_original_url,
      });
      onOpenChange(false);
    } catch (e) {
      toast.error(e?.message || "Failed to save photo.");
    } finally {
      setBusy(false);
    }
  };

  const handleRemove = async () => {
    try {
      setBusy(true);
      await profileAPI.deletePhoto();
      toast.success("Photo removed.");
      onChange?.({ avatar_url: "", avatar_original_url: "" });
      onOpenChange(false);
    } catch (e) {
      toast.error(e?.message || "Failed to remove photo.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg" data-testid="photo-upload-modal">
        <DialogHeader>
          <DialogTitle>Profile photo</DialogTitle>
          <DialogDescription>
            Take a new photo or upload an image, then pan + zoom to frame yourself.
            JPG, PNG, or WebP up to 8MB.
          </DialogDescription>
        </DialogHeader>

        {mode === "choose" && (
          <div className="space-y-3">
            {cameraError && (
              <div className="text-sm text-amber-800 bg-amber-50 border border-amber-200 rounded p-3 flex gap-2">
                <AlertTriangle className="h-4 w-4 mt-0.5 flex-shrink-0 text-amber-600" />
                <span>{cameraError}</span>
              </div>
            )}

            {cameraSupported ? (
              <Button
                onClick={startCamera}
                className="w-full justify-start bg-blue-600 hover:bg-blue-700 text-white"
                data-testid="photo-modal-camera-btn"
              >
                <Camera className="h-4 w-4 mr-2" /> Take a photo with camera
              </Button>
            ) : (
              <div className="text-sm text-slate-600 bg-slate-50 border border-slate-200 rounded p-3 flex gap-2">
                <AlertTriangle className="h-4 w-4 mt-0.5 flex-shrink-0 text-slate-500" />
                <span>
                  Camera capture isn't available in this browser. Open this page in Safari or
                  Chrome on your phone, or upload a saved photo below.
                </span>
              </div>
            )}

            <Button
              variant="outline"
              className="w-full justify-start"
              onClick={() => fileInputRef.current?.click()}
              data-testid="photo-modal-upload-btn"
            >
              <Upload className="h-4 w-4 mr-2" /> Upload photo from device
            </Button>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/jpeg,image/png,image/webp"
              onChange={onFilePicked}
              className="hidden"
            />

            {originalUrl && (
              <Button
                variant="outline"
                className="w-full justify-start"
                onClick={recropExisting}
                disabled={busy}
                data-testid="photo-modal-recrop-btn"
              >
                {busy ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <CropIcon className="h-4 w-4 mr-2" />}
                Re-crop existing photo
              </Button>
            )}

            {hasPhoto && (
              <Button
                variant="ghost"
                className="w-full justify-start text-red-600 hover:text-red-700 hover:bg-red-50"
                onClick={handleRemove}
                disabled={busy}
                data-testid="photo-modal-remove-btn"
              >
                {busy ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Trash2 className="h-4 w-4 mr-2" />}
                Remove current photo
              </Button>
            )}
          </div>
        )}

        {mode === "camera" && (
          <div className="space-y-3">
            <video
              ref={videoRef}
              autoPlay
              playsInline
              muted
              className="w-full rounded-lg bg-black aspect-square object-cover"
            />
            <canvas ref={canvasRef} className="hidden" />
            <div className="flex gap-2">
              <Button
                onClick={capturePhoto}
                className="flex-1 bg-blue-600 hover:bg-blue-700"
                data-testid="photo-modal-capture-btn"
              >
                <Camera className="h-4 w-4 mr-2" /> Capture
              </Button>
              <Button
                variant="outline"
                onClick={() => {
                  stopCamera();
                  setMode("choose");
                }}
              >
                Cancel
              </Button>
            </div>
          </div>
        )}

        {mode === "crop" && sourceUrl && (
          <div className="space-y-3">
            <div
              className="relative w-full bg-slate-900 rounded-lg overflow-hidden"
              style={{ height: 360 }}
              data-testid="photo-modal-cropper"
            >
              <Cropper
                image={sourceUrl}
                crop={crop}
                zoom={zoom}
                aspect={1}
                cropShape="rect"
                showGrid
                onCropChange={setCrop}
                onZoomChange={setZoom}
                onCropComplete={onCropComplete}
                restrictPosition
                objectFit="contain"
              />
            </div>
            <div className="flex items-center gap-3">
              <label className="text-xs text-slate-500 font-semibold uppercase tracking-wide w-12">
                Zoom
              </label>
              <input
                type="range"
                min={1}
                max={3}
                step={0.01}
                value={zoom}
                onChange={(e) => setZoom(parseFloat(e.target.value))}
                className="flex-1 accent-blue-600"
                data-testid="photo-modal-zoom-slider"
              />
            </div>
            <p className="text-xs text-slate-500">
              Drag to reposition · pinch or use the zoom slider to scale. We save the framed
              square AND the original so you can re-crop later.
            </p>
            <div className="flex gap-2">
              <Button
                onClick={handleSaveCrop}
                disabled={busy || !croppedAreaPixels}
                className="flex-1 bg-blue-600 hover:bg-blue-700"
                data-testid="photo-modal-save-btn"
              >
                {busy ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}
                Save photo
              </Button>
              <Button
                variant="outline"
                onClick={() => {
                  setSourceUrl("");
                  setOriginalBlob(null);
                  setMode("choose");
                }}
                disabled={busy}
              >
                <RefreshCw className="h-4 w-4 mr-2" /> Start over
              </Button>
            </div>
          </div>
        )}

        <DialogFooter />
      </DialogContent>
    </Dialog>
  );
}
